import { Button } from '../Button';
import { styled } from '../styled';
import useCopyToClipboard from '@shared/hooks/useCopyToClipboard';

// Outer wrapper card. Light surface-2 bg distinguishes it from the
// dialog body, dashed inner URL row signals "single-use copyable".
const Card = styled('div')({
  background: 'var(--qhs-surface-2)',
  border: '1px solid var(--qhs-border)',
  borderRadius: 10,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  alignSelf: 'stretch',
});

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'var(--qhs-surface)',
  border: '1.5px dashed var(--qhs-border-strong)',
  borderRadius: 8,
  padding: 12,
  fontFamily: "'Source Code Pro', 'JetBrains Mono', monospace",
  fontSize: 12.5,
  color: 'var(--qhs-text-1)',
  '& > .url': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '4px 6px',
  },
});

const IconBtn = styled('button')({
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  color: 'var(--qhs-text-2)',
  borderRadius: 6,
  width: 32,
  height: 32,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 140ms ease, color 140ms ease',
  '& svg': { width: 16, height: 16 },
  '&:hover': { background: 'var(--qhs-surface-hover, rgba(0,0,0,0.04))', color: 'var(--qhs-brand)' },
});

// Equal-width 50/50 buttons matching the mockup's symmetric pair.
// Buttons take flex:1 so they fill the row regardless of label length.
// flex-wrap guards long-locale labels: a button whose min-content
// width exceeds the row drops to its own line instead of pushing its
// sibling out of the card (seen with the Spanish open-in-new-tab copy).
const Actions = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  '& > button': { flex: 1, minHeight: 44, fontSize: 14 },
});

const Hint = styled('div')({
  marginTop: 6,
  fontSize: 12,
  color: 'var(--qhs-text-3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
});

const CopyIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const LaunchIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const LinkCard = ({ url, primaryAction, secondaryAction, hint, onCopySuccess }) => {
  const copyToClipboard = useCopyToClipboard();

  const handleCopy = async () => {
    const ok = await copyToClipboard(url);
    if (ok) onCopySuccess?.();
    secondaryAction?.onClick?.();
  };

  return (
    <Card>
      <Row>
        <span className="url" title={url}>
          {url}
        </span>
        <IconBtn type="button" onClick={handleCopy} aria-label={secondaryAction?.label ?? 'Copy'}>
          <CopyIcon />
        </IconBtn>
      </Row>
      <Actions>
        {primaryAction && (
          <Button variant="primary" startIcon={<LaunchIcon />} onClick={primaryAction.onClick}>
            {primaryAction.label}
          </Button>
        )}
        {secondaryAction && (
          <Button variant="secondary" startIcon={<CopyIcon />} onClick={handleCopy}>
            {secondaryAction.label}
          </Button>
        )}
      </Actions>
      {hint && (
        <Hint>
          <InfoIcon />
          {hint}
        </Hint>
      )}
    </Card>
  );
};

export default LinkCard;
